import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import styles from './styles.module.scss';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { isValidTagLabel } from '@utils-clean/validation';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { TagAPI } from '@apis/bff-api/tag';

interface Tag {
    label: string;
    active: boolean;
}

type EditTagDialogProps = {
    tagId: number | undefined;
    studyRef: string;
    onClose: () => void;
    onSubmit: () => void;
    existingTags: Tag[];
    activeInstances: number | undefined;
    tagLabel: string | null | undefined;
    isActive?: boolean;
};

interface IEditTagForm {
    tagLabel: string | null | undefined;
    isActive: boolean;
}

const COMPONENT_NAME = 'edit-tag-dialog';

const EditTagDialog = ({
    tagId,
    studyRef,
    onClose,
    onSubmit,
    existingTags,
    activeInstances,
    tagLabel,
    isActive,
}: EditTagDialogProps) => {
    const { t } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });

    const { trigger: updateTag, isMutating: isSaving } = TagAPI.useUpdateTag(studyRef);

    const {
        control: editTagControl,
        handleSubmit,
        formState: { isDirty, isValid },
        watch,
    } = useForm<IEditTagForm>({
        mode: 'all',
        defaultValues: {
            tagLabel,
            isActive,
        },
    });

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const successfulUpdate = (success: boolean) => {
        if (success) {
            ShowSuccess(t('actionsSuccessfulToast'));
            onSubmit();
        } else {
            ShowError(t('errorMessage'));
            onClose();
        }
    };

    const onlySpacesRegex = /^\s+$/gm;
    const labelIsOnlySpaces = onlySpacesRegex.test(watch('tagLabel') ?? '');

    return (
        <DraggableDialog
            dataTestId="edit-tag-dialog"
            onSubmit={async () =>
                handleSubmit(async (values: IEditTagForm) => {
                    if (!values.tagLabel || !tagId) {
                        return;
                    }
                    try {
                        const result = await updateTag({
                            id: tagId,
                            label: values.tagLabel,
                            active: values.isActive,
                        });

                        if (result) {
                            successfulUpdate(true);
                        } else {
                            successfulUpdate(false);
                        }
                    } catch {
                        successfulUpdate(false);
                    }
                })()
            }
            onClose={onCloseHandler}
            title={mtkTranslation('editTagDialog.title')}
            width={575}
            height={270}
            primaryBtnDisabled={!isDirty || !isValid || labelIsOnlySpaces}
            primaryBtnLabel="Save"
        >
            <div className={cn('k-w-full', 'k-h-full', 'k-d-flex-col')}>
                <div className="k-d-grid k-pb-2">
                    <InfoField
                        message={t('sm:monitoringToolkit.editTagDialog.message', {
                            num: activeInstances,
                            times: activeInstances === 1 ? 'time' : 'times',
                        })}
                        layout="stretch"
                        type="Warning"
                    />
                </div>

                <div className={styles.editFormWrapper}>
                    <CheckboxController
                        data-testid="is-active"
                        label={mtkTranslation('editTagDialog.active')}
                        name="isActive"
                        control={editTagControl}
                        disabled={isSaving}
                    />
                    <TextBoxController
                        control={editTagControl}
                        name="tagLabel"
                        data-testid="new-tag-label"
                        label={mtkTranslation('editTagDialog.label')}
                        disabled={isSaving}
                        maxLength={30}
                        required
                        validateRules={{
                            noInvalidCharacters: (value) => {
                                if (value && !isValidTagLabel(value as string)) {
                                    return mtkTranslation('addTagsModal.invalidChars');
                                }
                                return true;
                            },
                            checkExistingTags: (value) => {
                                if (!value) {
                                    return true;
                                }

                                const trimmed = (value as string).trim().toLowerCase();

                                if (trimmed === tagLabel) {
                                    return true;
                                }

                                if (existingTags.some((tag) => tag.label.toLowerCase() === trimmed)) {
                                    return mtkTranslation('addTagsModal.tagAlreadyExists');
                                }

                                return true;
                            },
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default EditTagDialog;
